/**
 * useDocumentZoom - Zoom controls for fixed-size documents
 * Supports button controls and two-finger trackpad gestures
 * Zoom range: 25% to 300% (0.25 to 3.0)
 */

import { useState, useCallback } from 'react'

const MIN_ZOOM = 0.25 // 25%
const MAX_ZOOM = 3.0 // 300%
const ZOOM_STEP = 0.25 // 25% increments

export interface UseDocumentZoomReturn {
	zoomLevel: number // 0.25 to 3.0 (25% to 300%)
	zoomIn: () => void // +25%
	zoomOut: () => void // -25%
	resetZoom: () => void // 100%
	setZoom: (level: number) => void // Direct zoom setter
	onWheel: (e: WheelEvent) => void // Two-finger gesture handler
}

export function useDocumentZoom(initialZoom: number = 1.0): UseDocumentZoomReturn {
	const [zoomLevel, setZoomLevel] = useState(initialZoom)

	const clampZoom = useCallback((zoom: number) => {
		return Math.max(MIN_ZOOM, Math.min(MAX_ZOOM, zoom))
	}, [])

	const zoomIn = useCallback(() => {
		setZoomLevel(prev => clampZoom(prev + ZOOM_STEP))
	}, [clampZoom])

	const zoomOut = useCallback(() => {
		setZoomLevel(prev => clampZoom(prev - ZOOM_STEP))
	}, [clampZoom])

	const resetZoom = useCallback(() => {
		setZoomLevel(1.0)
	}, [])

	const setZoom = useCallback((level: number) => {
		setZoomLevel(clampZoom(level))
	}, [clampZoom])

	/**
	 * Handle trackpad pinch-to-zoom (two-finger gesture)
	 * Detects ctrl+wheel events from trackpad
	 */
	const onWheel = useCallback((e: WheelEvent) => {
		// Only handle pinch-to-zoom (trackpad with ctrl/cmd key)
		if (!e.ctrlKey && !e.metaKey) return

		e.preventDefault()

		// deltaY is negative when zooming in (pinching out)
		// deltaY is positive when zooming out (pinching in)
		const delta = e.deltaY > 0 ? -ZOOM_STEP : ZOOM_STEP

		setZoomLevel(prev => clampZoom(prev + delta))
	}, [clampZoom])

	return {
		zoomLevel,
		zoomIn,
		zoomOut,
		resetZoom,
		setZoom,
		onWheel,
	}
}
